// @ts-nocheck
import React, { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import UserProfile from './Components/UserDashboard'
import TwitterDashboard from './Components/TwitterDashboard'
import TwitterCallback from './Components/TwitterCallback'
import Login from './Components/Login'
import AdminPortal from './Components/AdminPortal'
import { FacebookLoginClient } from '@greatsumini/react-facebook-login'
import axios from 'axios'
import './App.css'

const App = () => {
  // userData will store the logged-in user's data (from Facebook or Twitter)
  const [userData, setUserData] = useState(null)
  // authType indicates which provider was used ("facebook" or "twitter")
  const [authType, setAuthType] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const isUserDashboardAuth = (provider) =>
    provider === 'facebook' || provider === 'instagram_login'

  const defaultBackendUrl =
    typeof window !== 'undefined' ? window.location.origin : ''
  const backendUrl = process.env.REACT_APP_BACKEND_URL || defaultBackendUrl
  const isAdminConsoleEnabled =
    String(process.env.REACT_APP_ENABLE_ADMIN_CONSOLE || 'false')
      .trim()
      .toLowerCase() === 'true'

  useEffect(() => {
    axios
      .get(`${backendUrl}/me`, { withCredentials: true })
      .then((response) => {
        if (response.data && response.data.provider) {
          setUserData(response.data)
          // Use the provider field returned by the backend
          setAuthType(response.data.provider)
          setIsLoggedIn(true)
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false)
      })
  }, [backendUrl])

  // Also, update login state if userData and authType are set
  useEffect(() => {
    if (
      userData &&
      (authType === 'twitter' || isUserDashboardAuth(authType))
    ) {
      setIsLoggedIn(true)
    } else {
      setIsLoggedIn(false)
    }
  }, [userData, authType])

  // Update login state based on userData and authType
  // useEffect(() => {
  //   console.log('Checking login state...')
  //   if (userData && (authType === 'facebook' || authType === 'twitter')) {
  //     console.log(`User is logged in via ${authType}`)
  //     setIsLoggedIn(true)
  //   } else {
  //     console.log('No logged-in user found.')
  //     setIsLoggedIn(false)
  //   }
  // }, [userData, authType])

  const handleLogout = async () => {
    FacebookLoginClient.logout(async () => {
      try {
        await axios.post(`${backendUrl}/logout`, {}, { withCredentials: true })
        setUserData(null)
        setAuthType(null)
        localStorage.clear()
        setIsLoggedIn(false)
      } catch (error) {
        console.error('Error logging out:', error)
      }
    })
  }

  const handleDeleteData = async (options = {}) => {
    const delayMs =
      typeof options.delayMs === 'number' ? options.delayMs : 2000
    try {
      await axios.delete(`${backendUrl}/me`, { withCredentials: true })
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
      }
      setUserData(null)
      setAuthType(null)
      localStorage.clear()
      setIsLoggedIn(false)
      return true
    } catch (error) {
      console.error('Data deletion failed:', error)
      return false
    }
  }

  // Callback for Facebook login – normalizes data and sets authType to "facebook"
  const setFbDetails = (data) => {
    const normalizedData = Array.isArray(data) ? data : [data]
    if (normalizedData.length > 0 && normalizedData[0].fb_user_id) {
      setUserData(normalizedData[0])
      setAuthType('facebook')
      setIsLoggedIn(true)
    } else {
      setUserData(null)
      setAuthType(null)
      setIsLoggedIn(false)
    }
  }

  // Callback for Twitter login – this should be called from your TwitterCallback component
  const setTwitterDetails = (data) => {
    if (data && data.twitter_user_id) {
      setUserData(data)
      setAuthType('twitter')
      setIsLoggedIn(true)
    } else {
      setUserData(null)
      setAuthType(null)
      setIsLoggedIn(false)
    }
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/tennis"
          element={
            isLoading ? (
              <div>Loading...</div>
            ) : isLoggedIn && isUserDashboardAuth(authType) ? (
              <UserProfile
                response={userData}
                handleLogout={handleLogout}
                handleDeleteData={handleDeleteData}
              />
            ) : (
              <Login
                setFbResponse={setUserData}
                setFbDetails={setFbDetails}
                variant="tennis"
              />
            )
          }
        />
        <Route
          path="/rugby"
          element={
            isLoading ? (
              <div>Loading...</div>
            ) : isLoggedIn && isUserDashboardAuth(authType) ? (
              <UserProfile
                response={userData}
                handleLogout={handleLogout}
                handleDeleteData={handleDeleteData}
              />
            ) : (
              <Login
                setFbResponse={setUserData}
                setFbDetails={setFbDetails}
                variant="rugby"
              />
            )
          }
        />
        <Route
          path="/worldrugby"
          element={
            isLoading ? (
              <div>Loading...</div>
            ) : isLoggedIn && isUserDashboardAuth(authType) ? (
              <UserProfile
                response={userData}
                handleLogout={handleLogout}
                handleDeleteData={handleDeleteData}
              />
            ) : (
              <Login
                setFbResponse={setUserData}
                setFbDetails={setFbDetails}
                variant="worldrugby"
              />
            )
          }
        />
        {/* Twitter authentication routes */}
        <Route
          path="/twitter/callback"
          element={<TwitterCallback setTwitterDetails={setTwitterDetails} />}
        />

        {isAdminConsoleEnabled ? (
          <Route path="/admin" element={<AdminPortal />} />
        ) : (
          <Route path="/admin" element={<Navigate to="/" replace />} />
        )}

        {/* Twitter dashboard route */}
        <Route
          path="/twitter/dashboard"
          element={
            <TwitterDashboard userData={userData} handleLogout={handleLogout} />
          }
        />

        {/* Root route: choose dashboard based on authType */}
        <Route
          path="/"
          element={
            isLoading ? (
              <div>Loading...</div>
            ) : isLoggedIn ? (
              authType === 'twitter' ? (
                <TwitterDashboard
                  userData={userData}
                  handleLogout={handleLogout}
                />
              ) : (
                <UserProfile
                  response={userData}
                  handleLogout={handleLogout}
                  handleDeleteData={handleDeleteData}
                />
              )
            ) : (
              <Login setFbResponse={setUserData} setFbDetails={setFbDetails} />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  )
}

export default App
